import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowRight, FiCheckCircle, FiMail, FiRefreshCw, FiShield, FiZap } from 'react-icons/fi';
import Navbar from '../shared/Navbar.jsx';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import '../home/Home.css';
import './VerifyEmail.css';

const apiBaseUrl = getApiBaseUrl();

function VerifyEmail() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const initialEmail = searchParams.get('email') || '';

	const [digits, setDigits] = useState(['', '', '', '', '', '']);
	const [email, setEmail] = useState(initialEmail);
	const [loading, setLoading] = useState(false);
	const [resending, setResending] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [cooldown, setCooldown] = useState(0);
	const inputRefs = useRef([]);

	useEffect(() => {
		inputRefs.current[0]?.focus();
	}, []);

	useEffect(() => {
		if (cooldown <= 0) return;
		const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
		return () => clearTimeout(t);
	}, [cooldown]);

	const code = digits.join('');

	const handleDigitChange = (index, value) => {
		const cleaned = value.replace(/\D/g, '').slice(-1);
		const next = [...digits];
		next[index] = cleaned;
		setDigits(next);
		setError('');
		if (cleaned && index < 5) inputRefs.current[index + 1]?.focus();
	};

	const handleKeyDown = (index, e) => {
		if (e.key === 'Backspace' && !digits[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e) => {
		e.preventDefault();
		const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
		if (!pasted) return;
		const next = ['', '', '', '', '', ''];
		for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
		setDigits(next);
		inputRefs.current[Math.min(pasted.length, 5)]?.focus();
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (code.length !== 6) { setError('Entrez les 6 chiffres du code.'); return; }
		setLoading(true);
		setError('');
		try {
			const res = await fetch(`${apiBaseUrl}/api/users/verify-email`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email, code })
			});
			const data = await res.json();
			if (!res.ok || !data.success) { setError(data.message || 'Code incorrect.'); return; }
			window.localStorage.setItem('exp-auth-user', JSON.stringify(data.user));
			window.localStorage.setItem('exp-user-profile', JSON.stringify(data.user));
			navigate('/');
		} catch { setError('Impossible de contacter le serveur.'); }
		finally { setLoading(false); }
	};

	const handleResend = async () => {
		if (cooldown > 0 || resending) return;
		setResending(true);
		setError('');
		setSuccess('');
		try {
			const res = await fetch(`${apiBaseUrl}/api/users/resend-verification`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email })
			});
			const data = await res.json();
			if (!res.ok || !data.success) { setError(data.message || 'Erreur lors du renvoi.'); return; }
			setSuccess('Nouveau code envoyé — vérifiez votre boîte mail.');
			setCooldown(60);
			setDigits(['', '', '', '', '', '']);
			inputRefs.current[0]?.focus();
		} catch { setError('Impossible de contacter le serveur.'); }
		finally { setResending(false); }
	};

	return (
		<div className="vx-verify-page">

			<Navbar minimal />

			<main className="vx-verify-main">

				{/* ── LEFT — dark panel ── */}
				<div className="vx-verify-left">
					<div className="vx-verify-left-inner">
						<span className="vx-verify-kicker">
							<FiZap size={13} aria-hidden="true" />
							Vérification
						</span>
						<h1 className="vx-verify-title">
							Confirmez<br /><em>votre identité.</em>
						</h1>
						<p className="vx-verify-subtitle">
							Un code à 6 chiffres vous a été envoyé par email. Entrez-le pour activer votre compte UltimateXDrive.
						</p>
						<div className="vx-verify-info-block">
							<p className="vx-verify-info-title">Comment procéder ?</p>
							<ul className="vx-verify-info-list">
								<li><FiMail size={13} aria-hidden="true" /> Ouvrez votre boîte mail</li>
								<li><FiShield size={13} aria-hidden="true" /> Repérez le code à 6 chiffres</li>
								<li><FiCheckCircle size={13} aria-hidden="true" /> Collez-le dans les cases ci-contre</li>
							</ul>
							<p className="vx-verify-info-note">
								Le code est valable <strong>10 minutes</strong>.<br />
								Pas de mail reçu ? Vérifiez vos spams.
							</p>
						</div>
					</div>
				</div>

				{/* ── RIGHT — white form ── */}
				<div className="vx-verify-right">
					<div className="vx-verify-right-inner">

						<div className="vx-verify-form-head">
							<span className="vx-section-kicker">Sécurité</span>
							<h2 className="vx-verify-form-title">Entrez le code reçu</h2>
							<p className="vx-verify-form-sub">
								Code envoyé à <strong className="vx-verify-email-hl">{email || 'votre adresse email'}</strong>
							</p>
						</div>

						<form className="vx-verify-form" onSubmit={handleSubmit} noValidate>

							{!initialEmail && (
								<div className="vx-verify-field">
									<label className="vx-verify-label" htmlFor="vx-verify-email-input">
										Adresse e-mail
									</label>
									<input
										id="vx-verify-email-input"
										type="email"
										className="vx-verify-input"
										placeholder="vous@ultimatexdrive.com"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										autoComplete="email"
									/>
								</div>
							)}

							<div className="vx-verify-code-wrap">
								<p className="vx-verify-label" style={{ marginBottom: '0.75rem' }}>
									Code de vérification
								</p>
								<div className="vx-verify-digits" onPaste={handlePaste}>
									{digits.map((d, i) => (
										<input
											key={i}
											ref={(el) => (inputRefs.current[i] = el)}
											type="text"
											inputMode="numeric"
											maxLength={1}
											value={d}
											onChange={(e) => handleDigitChange(i, e.target.value)}
											onKeyDown={(e) => handleKeyDown(i, e)}
											className={`vx-verify-digit${d ? ' is-filled' : ''}`}
											aria-label={`Chiffre ${i + 1}`}
										/>
									))}
								</div>
							</div>

							{error && <p className="vx-verify-error" role="alert">⚠ {error}</p>}
							{success && <p className="vx-verify-success" role="status">✓ {success}</p>}

							<button
								type="submit"
								className="vx-btn vx-btn-solid vx-verify-submit"
								disabled={loading || code.length !== 6}
							>
								{loading ? (
									<span className="vx-verify-loading">
										<span className="vx-verify-dot" />
										<span className="vx-verify-dot" />
										<span className="vx-verify-dot" />
										Vérification en cours…
									</span>
								) : (
									<>Confirmer le code <FiArrowRight size={14} aria-hidden="true" /></>
								)}
							</button>

							<div className="vx-verify-resend-row">
								<FiRefreshCw size={12} aria-hidden="true" />
								<span>Pas reçu le code ?</span>
								<button
									type="button"
									onClick={handleResend}
									disabled={cooldown > 0 || resending}
									className="vx-verify-resend-btn"
								>
									{resending
										? 'Envoi…'
										: cooldown > 0
										? `Renvoyer dans ${cooldown}s`
										: 'Renvoyer le code'}
								</button>
							</div>

						</form>

						<p className="vx-verify-footer">
							Mauvais compte ? <Link to="/signup">Retour à l'inscription</Link>
						</p>

					</div>
				</div>

			</main>
		</div>
	);
}

export default VerifyEmail;
